import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    loginWithEmail, registerWithEmail, resetPassword, 
    loginWithGoogle, setAuthUser, clearAdminError 
} from '../../store/slices/adminSlice';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';

const AdminLoginPage = () => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { authLoading, error, infoMessage } = useSelector((state) => state.admin);

    const isDev = process.env.NODE_ENV === 'development';

    useEffect(() => {
        // Do not auto-redirect or listen if we're blocking access in production
        if (!isDev) return;

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                dispatch(setAuthUser({ uid: user.uid, email: user.email, displayName: user.displayName }));
                navigate('/admin/dashboard');
            } else {
                dispatch(setAuthUser(null));
            }
        });
        return () => unsubscribe();
    }, [dispatch, navigate, isDev]);

    const handleSwitchMode = (newMode) => {
        setMode(newMode);
        dispatch(clearAdminError());
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(clearAdminError());
        if (!isDev) return; // Safeguard

        if (mode === 'login') {
            if (email && password) {
                dispatch(loginWithEmail({ email, password }));
            }
        } else if (mode === 'register') {
            if (email && password) {
                dispatch(registerWithEmail({ email, password, name }));
            }
        } else if (mode === 'forgot') {
            if (email) {
                dispatch(resetPassword(email));
            }
        }
    };

    if (!isDev) {
        return (
            <div className="admin-login-page min-vh-100 bg-gray-50 flex-center py-40 font-sans position-relative">
                <div className="position-absolute top-0 end-0 m-24">
                    <LanguageSwitcher />
                </div>
                <div className="container" style={{ maxWidth: '440px' }}>
                    <div className="bg-white p-40 rounded-24 shadow-sm border text-center">
                        <div className="w-64 h-64 rounded-circle bg-gray-100 text-gray-500 flex-center mx-auto mb-24 shadow-2xs">
                            <i className="ph ph-wrench text-3xl"></i>
                        </div>
                        <h3 className="fw-bold text-gray-900 mb-12">{t('auth.devTitle')}</h3>
                        <p className="text-gray-500 mb-0">
                            {t('auth.devDesc')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-login-page min-vh-100 bg-gray-50 flex-center py-40 font-sans position-relative">
            <div className="position-absolute top-0 end-0 m-24">
                <LanguageSwitcher />
            </div>
            
            <div className="container" style={{ maxWidth: '440px' }}>
                <div className="bg-white p-32 rounded-24 shadow-sm border">
                    <div className="text-center mb-28">
                        <div className="w-56 h-56 rounded-16 bg-main-100 text-main-600 flex-center mx-auto mb-16 shadow-2xs">
                            <i className="ph ph-storefront text-3xl"></i>
                        </div>
                        <h4 className="fw-bold text-gray-900 mb-6">
                            {mode === 'login' && t('auth.loginTitle')}
                            {mode === 'register' && t('auth.registerTitle')}
                            {mode === 'forgot' && t('auth.forgotTitle')}
                        </h4>
                        <p className="text-gray-500 text-xs mb-0">
                            {mode === 'login' && t('auth.loginDesc')}
                            {mode === 'register' && t('auth.registerDesc')}
                            {mode === 'forgot' && t('auth.forgotDesc')}
                        </p>
                    </div>

                    {error && (
                        <div className="alert alert-danger py-10 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                            <i className="ph ph-warning-circle text-md flex-shrink-0"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    {infoMessage && (
                        <div className="alert alert-success py-10 px-16 text-xs rounded-12 mb-20 flex-align gap-8" role="alert">
                            <i className="ph ph-check-circle text-md flex-shrink-0"></i>
                            <span>{infoMessage}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {mode === 'register' && (
                            <div className="mb-16">
                                <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">{t('auth.nameLabel')}</label>
                                <input 
                                    type="text" 
                                    className="form-control rounded-12 py-10 px-16 text-sm" 
                                    placeholder={t('auth.namePlaceholder')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                        )}

                        <div className="mb-16">
                            <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">{t('auth.emailLabel')}</label>
                            <input 
                                type="email" 
                                className="form-control rounded-12 py-10 px-16 text-sm" 
                                placeholder={t('auth.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        {mode !== 'forgot' && (
                            <div className="mb-24">
                                <label className="form-label text-xs fw-bold text-gray-700 text-uppercase">{t('auth.passwordLabel')}</label>
                                <input 
                                    type="password" 
                                    className="form-control rounded-12 py-10 px-16 text-sm" 
                                    placeholder={t('auth.passwordPlaceholder')}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                />
                            </div>
                        )}

                        <button 
                            type="submit" 
                            className="btn btn-main w-100 rounded-pill py-12 fw-bold mb-16 shadow-xs flex-center gap-8"
                            disabled={authLoading}
                        >
                            {authLoading ? (
                                <div className="spinner-border spinner-border-sm" role="status"></div>
                            ) : (
                                mode === 'login' ? t('auth.submitLogin') :
                                mode === 'register' ? t('auth.submitRegister') :
                                t('auth.submitForgot')
                            )}
                        </button>

                        {mode === 'login' && (
                            <button 
                                type="button"
                                className="btn btn-outline-gray-200 text-gray-700 w-100 rounded-pill py-12 fw-semibold flex-center gap-8 hover-bg-gray-50 transition-2"
                                onClick={() => dispatch(loginWithGoogle())}
                                disabled={authLoading}
                            >
                                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-16 h-16" />
                                {t('auth.googleLogin')}
                            </button>
                        )}
                    </form>

                    <div className="mt-32 pt-24 border-top text-center">
                        {mode === 'login' && (
                            <>
                                <button type="button" className="btn btn-link text-gray-500 hover-text-main-600 text-sm fw-medium text-decoration-none p-0 mb-12 d-block mx-auto" onClick={() => handleSwitchMode('forgot')}>
                                    {t('auth.linkForgot')}
                                </button>
                                <button type="button" className="btn btn-link text-gray-600 hover-text-main-600 text-sm fw-medium text-decoration-none p-0 d-block mx-auto" onClick={() => handleSwitchMode('register')}>
                                    {t('auth.linkNoAccount')}
                                </button>
                            </>
                        )}

                        {mode === 'register' && (
                            <button type="button" className="btn btn-link text-gray-600 hover-text-main-600 text-sm fw-medium text-decoration-none p-0 d-block mx-auto" onClick={() => handleSwitchMode('login')}>
                                {t('auth.linkHasAccount')}
                            </button>
                        )}

                        {mode === 'forgot' && (
                            <button type="button" className="btn btn-link text-gray-600 hover-text-main-600 text-sm fw-medium text-decoration-none p-0 flex-align justify-content-center gap-6 mx-auto" onClick={() => handleSwitchMode('login')}>
                                <i className="ph ph-arrow-left"></i>
                                {t('auth.linkBackLogin')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLoginPage;
